import { generatePath } from 'react-router';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { pickFields } from 'utils/helpers';
import { addParamsToUrl } from 'utils/query';
import { Tenant, ReportData, BaseResponse, PaginationResponse } from 'types';
import {
  providesList,
  cacheByIdArg,
  invalidatesList,
  cacheByIdArgProperty,
  cacheListAndByIdArgProperty,
} from 'utils/query-cache';

import { apiQuery } from '../../query';

import {
  ReportRequest,
  ReportResponse,
  DuplicateRequest,
  TenantListRequest,
  TenantVersionsResponse,
  EmailDomainChangeResult,
  EmailDomainChangeRequest,
} from './types';

const tagType = 'TENANT';
const reportTagType = 'TENANT_REPORT';

const updatedFields: (keyof Omit<Tenant, 'id'>)[] = [
  'zip',
  'name',
  'city',
  'logo',
  'code',
  'state',
  'county',
  'address',
  'siteUrl',
  'siteLogo',
  'isActive',
  'projectId',
  'languages',
  'publicName',
  'reportsData',
  'landingText',
  'contactEmail',
  'gameVideoUrl',
  'mcirIdEnabled',
  'gameConfigUrl',
  'ssoIdentifier',
  'ssoUserEnabled',
  'libraryEnabled',
  'ssoAdminEnabled',
  'privacyPolicyUrl',
  'childEnteringTypes',
  'auth0SamlConnection',
  'libraryPublicApiEnabled',
] as const;

export const tenantApi = createApi({
  baseQuery: apiQuery,
  reducerPath: 'tenantApi',
  tagTypes: [tagType, reportTagType],
  endpoints: (build) => ({
    delete: build.mutation<void, string>({
      invalidatesTags: invalidatesList(tagType),
      query: (id) => ({
        method: 'delete',
        url: URL.TENANT_DETAILS.replace(':id', id),
      }),
    }),

    autoTranslate: build.mutation<void, Tenant>({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      query: ({ id }) => ({
        method: 'post',
        url: generatePath(URL.TENANT_AUTO_TRANSLATE, { id }),
      }),
    }),

    create: build.mutation<Tenant, Omit<Tenant, 'id'>>({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: BaseResponse<Tenant>) => response.data,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.TENANT_LIST,
      }),
    }),

    details: build.query<Tenant, { id: string }>({
      providesTags: cacheByIdArgProperty(tagType),
      transformResponse: (response: BaseResponse<Tenant>) => response.data,
      query: ({ id }) => ({
        method: 'get',
        url: URL.TENANT_DETAILS.replace(':id', id),
      }),
    }),

    usedVersions: build.query<number[], string>({
      providesTags: cacheByIdArg(tagType),
      transformResponse: (response: TenantVersionsResponse) =>
        response.data.usedVersions,
      query: (id) => ({
        method: 'get',
        url: generatePath(URL.TENANT_PROFILE_FORM_VERSIONS, { id }),
      }),
    }),

    duplicate: build.mutation<Tenant, DuplicateRequest>({
      invalidatesTags: invalidatesList(tagType),
      transformResponse: (response: BaseResponse<Tenant>) => response.data,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: generatePath(URL.TENANT_DUPLICATE, { id }),
      }),
    }),

    list: build.query<PaginationResponse<Tenant>, TenantListRequest | void>({
      providesTags: providesList(tagType),
      transformResponse: (response: BaseResponse<PaginationResponse<Tenant>>) =>
        response.data,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.TENANT_LIST, params ?? undefined),
      }),
    }),

    report: build.query<ReportData, ReportRequest>({
      providesTags: [reportTagType],
      transformResponse: (response: ReportResponse) => response.data,
      query: ({ id, reportId, ...rest }) => ({
        method: 'get',
        url: addParamsToUrl(
          generatePath(URL.TENANT_REPORT, { id, reportId }),
          rest
        ),
      }),
    }),

    changeEmailDomain: build.mutation<
      EmailDomainChangeResult,
      EmailDomainChangeRequest
    >({
      transformResponse: (response: BaseResponse<EmailDomainChangeResult>) =>
        response.data,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: generatePath(URL.TENANT_EMAIL_DOMAIN_CHANGE, { id }),
      }),
    }),

    update: build.mutation<Tenant, Tenant>({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      transformResponse: (response: BaseResponse<Tenant>) => response.data,
      query: ({ id, ...tenant }) => ({
        method: 'patch',
        url: URL.TENANT_DETAILS.replace(':id', id),
        data: pickFields<Omit<Tenant, 'id'>>(tenant, updatedFields),
      }),
    }),
  }),
});

export const {
  useListQuery,
  useReportQuery,
  useDetailsQuery,
  useLazyListQuery,
  useCreateMutation,
  useUpdateMutation,
  useDeleteMutation,
  useLazyDetailsQuery,
  useDuplicateMutation,
  useUsedVersionsQuery,
  useAutoTranslateMutation,
  useChangeEmailDomainMutation,
} = tenantApi;

export type { ReportRequest };

export { reportTagType as tenantReportTagType };
